import {
  ArrowUpRight,
  Store,
  Boxes,
  ClipboardList,
  Wallet,
  BadgeCheck,
  Building2,
  Truck,
} from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import delresstroImg from '@/images/Delresstro-hero.png';

// Кабинет поставщика, отдельное устанавливаемое приложение (PWA).
// Локально http://localhost:5176, на проде задаётся через VITE_SUPPLIER_URL (поддомен).
const SUPPLIER_APP = import.meta.env.VITE_SUPPLIER_URL?.trim() || 'http://localhost:5176';

const STEPS = [
  {
    icon: Boxes,
    title: 'Заполните каталог',
    text: 'Бренд, логотип, категории и товары с ценами, остатками и условиями поставки.',
  },
  {
    icon: Store,
    title: 'Вас видят заведения',
    text: 'Рестораны и кафе на Resstro находят вас в разделе «Закуп» и заказывают прямо из кассы.',
  },
  {
    icon: ClipboardList,
    title: 'Принимаете заказы',
    text: 'Заявки, история и статусы в одном кабинете. Без звонков, бумаги и переписок в мессенджерах.',
  },
];

const FEATURES = [
  {
    icon: BadgeCheck,
    title: 'Готовая база клиентов',
    text: 'Заведения уже работают в Resstro. Вы получаете спрос без вложений в рекламу.',
  },
  {
    icon: Building2,
    title: 'Карточка компании',
    text: 'Реквизиты РФ, юр. форма, банк: всё для официальной работы с ресторанами.',
  },
  {
    icon: Wallet,
    title: 'Расчёты и долги',
    text: 'Условия оплаты, отсрочка, учёт дебиторки по каждому заведению.',
  },
  {
    icon: Truck,
    title: 'Логистика',
    text: 'Регионы доставки, график, минимальная сумма и порог бесплатной доставки.',
  },
];

export default function Suppliers() {
  return (
    <>
      <Header />

      {/* Hero */}
      <main>
        <section className="bg-paper section-divider">
          <div className="container-site py-16 sm:py-20 lg:py-24">
            <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.05fr] lg:gap-12">
              <div className="max-w-xl">
                <span className="eyebrow">
                  <Store size={13} strokeWidth={2.2} />
                  Resstro для поставщиков
                </span>
                <h1 className="display mt-6">
                  Продавайте продукты ресторанам
                  <br />
                  <span className="text-ink/40">прямо в их кассе</span>
                </h1>
                <p className="lead mt-7">
                  Заведения, которые уже работают на Resstro, заказывают товары у вас в пару касаний.
                  Вы ведёте каталог и принимаете заявки, мы приводим клиентов.
                </p>

                <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a href={`${SUPPLIER_APP}/signup`} className="btn-primary px-6 py-3.5">
                    Стать поставщиком
                    <ArrowUpRight size={16} strokeWidth={2} />
                  </a>
                  <a href={`${SUPPLIER_APP}/login`} className="btn-outline px-6 py-3.5">
                    Войти в кабинет
                  </a>
                </div>
                <p className="mt-5 text-[13px] text-ink/50">
                  3 дня бесплатно · затем 2 999 ₽/мес · без привязки карты
                </p>
              </div>

              <div className="order-first lg:order-last">
                <img
                  src={delresstroImg}
                  alt="Курьер Resstro доставляет заказ ресторану"
                  className="mx-auto w-full max-w-[16rem] sm:max-w-xs lg:max-w-none"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Как это работает */}
        <section className="section-divider">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Как это работает</span>
            <h2 className="h2 mt-5 max-w-2xl">Три шага до первых заказов</h2>

            <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-3">
              {STEPS.map((s, i) => (
                <div key={s.title} className="bg-white p-8">
                  <div className="flex items-center justify-between">
                    <span className="grid h-11 w-11 place-items-center bg-paper text-ink">
                      <s.icon size={20} strokeWidth={1.8} />
                    </span>
                    <span className="text-[13px] font-medium text-ink/30">0{i + 1}</span>
                  </div>
                  <h3 className="h3 mt-6">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink/65">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Что вы получаете */}
        <section className="section-divider bg-paper">
          <div className="container-site py-20 sm:py-28">
            <span className="eyebrow">Возможности</span>
            <h2 className="h2 mt-5 max-w-2xl">Всё для работы с заведениями</h2>

            <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex gap-5 bg-white p-8">
                  <span className="grid h-11 w-11 shrink-0 place-items-center bg-paper text-orange">
                    <f.icon size={20} strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="h3">{f.title}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-ink/65">{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Тариф */}
        <section className="section-divider">
          <div className="container-site py-20 sm:py-28">
            <div className="mx-auto max-w-2xl border border-line bg-white p-10 text-center sm:p-14">
              <span className="eyebrow justify-center">Тариф</span>
              <div className="mt-7 flex items-end justify-center gap-2">
                <span className="text-[56px] font-medium leading-none tracking-tightest text-ink">
                  2 999 ₽
                </span>
                <span className="pb-2 text-ink/50">/ мес</span>
              </div>
              <p className="mt-4 text-[15px] text-ink/65">
                Первые <span className="font-medium text-ink">3 дня бесплатно</span>. Один тариф,
                без лимитов на товары, категории и заявки.
              </p>
              <a href={`${SUPPLIER_APP}/signup`} className="btn-primary mt-9 px-7 py-3.5">
                Начать бесплатно
                <ArrowUpRight size={16} strokeWidth={2} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
